type ApiSuccess<T> = { success: true; data: T }
type ApiFailure = { success: false; error: { code: string; message: string } }

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    ...init,
  })

  return response.json() as Promise<ApiResponse<T>>
}

export type SignupPayload = {
  firstName: string
  lastName: string
  email: string
  password: string
  workspaceName: string
}

export type SignupResponse = {
  user: { id: string; name: string; email: string }
  workspace: { id: string; name: string; slug: string }
}

export type HeaderImage = {
  id: string
  url: string
  thumbUrl: string
  alt: string
  photographerName: string
  photographerUrl: string
}

export type Workspace = {
  id: string
  name: string
  slug: string
  logo: string | null
  brandColor: string
  headerImage: HeaderImage | null
  plan: string
  hidePoweredBy: boolean
  memberCount: number
  role: 'admin' | 'editor'
  modules: Array<'knowledge' | 'support' | 'crm'>
  createdAt?: string
  updatedAt?: string
}

export type UpdateWorkspacePayload = {
  name?: string
  slug?: string
  brandColor?: string
  hidePoweredBy?: boolean
  headerImage?: HeaderImage | null
  logo?: File | null
}

export async function signup(payload: SignupPayload) {
  return apiFetch<SignupResponse>('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getWorkspaces() {
  return apiFetch<Workspace[]>('/api/workspace')
}

export async function createWorkspace(name: string) {
  const formData = new FormData()
  formData.append('name', name)

  const response = await fetch('/api/workspace', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  return response.json() as Promise<ApiResponse<Workspace>>
}

export async function updateWorkspace(workspaceId: string, payload: UpdateWorkspacePayload) {
  const hasFile = payload.logo instanceof File
  const hasJsonFields =
    payload.name !== undefined ||
    payload.slug !== undefined ||
    payload.brandColor !== undefined ||
    payload.hidePoweredBy !== undefined ||
    payload.headerImage !== undefined

  if (hasFile || hasJsonFields) {
    const formData = new FormData()
    if (payload.name !== undefined) formData.append('name', payload.name)
    if (payload.slug !== undefined) formData.append('slug', payload.slug)
    if (payload.brandColor !== undefined) formData.append('brandColor', payload.brandColor)
    if (payload.hidePoweredBy !== undefined) {
      formData.append('hidePoweredBy', String(payload.hidePoweredBy))
    }
    if (payload.headerImage !== undefined) {
      formData.append('headerImage', JSON.stringify(payload.headerImage))
    }
    if (payload.logo) formData.append('logo', payload.logo)

    const response = await fetch(`/api/workspace?workspaceId=${encodeURIComponent(workspaceId)}`, {
      method: 'PUT',
      credentials: 'include',
      body: formData,
    })

    return response.json() as Promise<ApiResponse<Workspace>>
  }

  return apiFetch<Workspace>(`/api/workspace?workspaceId=${encodeURIComponent(workspaceId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function deleteWorkspace(workspaceId: string) {
  return apiFetch<{ deleted: boolean }>(
    `/api/workspace?workspaceId=${encodeURIComponent(workspaceId)}`,
    { method: 'DELETE' },
  )
}

export function isWorkspaceAdmin(workspace: Workspace) {
  return workspace.role === 'admin'
}

export async function uploadUserAvatar(file: File) {
  const formData = new FormData()
  formData.append('avatar', file)

  const response = await fetch('/api/user/avatar', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  return response.json() as Promise<ApiResponse<{ image: string }>>
}

export async function setUserAvatarPreset(imageUrl: string) {
  const response = await fetch('/api/user/avatar/preset', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageUrl }),
  })

  return response.json() as Promise<ApiResponse<{ image: string }>>
}
